import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const KO_VERSION = 'NKRV';

function testamentLabel(testament, sortOrder) {
  const raw = String(testament || '').trim().toUpperCase();
  if (raw === 'OT' || raw === 'OLD') return '구약';
  if (raw === 'NT' || raw === 'NEW') return '신약';
  return (sortOrder ?? 999) <= 39 ? '구약' : '신약';
}

function pickAliasName(aliases, languageCode) {
  const list = Array.isArray(aliases) ? aliases : [];
  const want = String(languageCode || '').toLowerCase();
  const byLang = list.find((a) => String(a.language_code || '').toLowerCase() === want);
  if (byLang?.alias_name) return byLang.alias_name;
  if (want === 'ko') {
    const byNull = list.find((a) => a.language_code == null || a.language_code === '');
    if (byNull?.alias_name) return byNull.alias_name;
  }
  return '';
}

const BibleList = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [enVersionCode, setEnVersionCode] = useState('');
  const [statsByKey, setStatsByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [testamentFilter, setTestamentFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [{ data: versions, error: verErr }, { data: bookRows, error: bookErr }] =
          await Promise.all([
            supabase
              .from('bible_versions')
              .select('code,name,language_code,is_active')
              .eq('is_active', true)
              .order('code', { ascending: true }),
            supabase
              .from('bible_books')
              .select(
                'code,display_name,testament,sort_order,std_chapter,std_verse,bible_book_aliases(alias_name,language_code,is_primary)'
              )
              .order('sort_order', { ascending: true }),
          ]);

        if (verErr) throw verErr;
        if (bookErr) throw bookErr;

        const enVer =
          (versions || []).find((v) => String(v.language_code || '').toLowerCase() === 'en')?.code ||
          (versions || []).find((v) => v.code !== KO_VERSION)?.code ||
          '';

        let statsMap = {};
        const { data: statsRows, error: statsErr } = await supabase
          .from('bible_book_version_stats')
          .select('version_code,book_code,chapter_count,verse_count');

        if (statsErr) {
          // 뷰가 없으면 버전별 행을 직접 집계 (관리자용, 한 번 로드)
          const versionCodes = [KO_VERSION, enVer].filter(Boolean);
          for (const vc of versionCodes) {
            const { data: verseRows, error: verseErr } = await supabase
              .from('bible_version_verses')
              .select('book_code,chapter_number')
              .eq('version_code', vc);
            if (verseErr) throw verseErr;
            const agg = {};
            (verseRows || []).forEach((row) => {
              const key = `${vc}::${row.book_code}`;
              if (!agg[key]) agg[key] = { chapters: new Set(), verses: 0 };
              agg[key].chapters.add(row.chapter_number);
              agg[key].verses += 1;
            });
            Object.entries(agg).forEach(([key, val]) => {
              statsMap[key] = {
                chapter_count: val.chapters.size,
                verse_count: val.verses,
              };
            });
          }
        } else {
          (statsRows || []).forEach((row) => {
            statsMap[`${row.version_code}::${row.book_code}`] = {
              chapter_count: row.chapter_count,
              verse_count: row.verse_count,
            };
          });
        }

        if (cancelled) return;
        setEnVersionCode(enVer);
        setStatsByKey(statsMap);
        setBooks(bookRows || []);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError(e.message || '성경 목록을 불러오지 못했습니다.');
          setBooks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    return (books || [])
      .map((book) => {
        const aliases = book.bible_book_aliases || [];
        const primaryAliases = aliases.filter((a) => a.is_primary !== false);
        const koName =
          pickAliasName(primaryAliases, 'ko') ||
          pickAliasName(aliases, 'ko') ||
          book.display_name;
        const enName =
          pickAliasName(primaryAliases, 'en') ||
          pickAliasName(aliases, 'en') ||
          book.code;
        const koStat = statsByKey[`${KO_VERSION}::${book.code}`];
        const enStat = enVersionCode
          ? statsByKey[`${enVersionCode}::${book.code}`]
          : null;
        return {
          code: book.code,
          koName,
          enName,
          testament: testamentLabel(book.testament, book.sort_order),
          testamentRaw: book.testament,
          sort_order: book.sort_order,
          stdChapter: book.std_chapter ?? null,
          stdVerse: book.std_verse ?? null,
          nkrvVerses: koStat?.verse_count ?? 0,
          enVerses: enStat?.verse_count ?? 0,
        };
      })
      .filter((row) => {
        if (testamentFilter === 'all') return true;
        return row.testament === (testamentFilter === 'ot' ? '구약' : '신약');
      });
  }, [books, statsByKey, enVersionCode, testamentFilter]);

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center flex-wrap">
        <h6 className="m-0 font-weight-bold text-primary">성경 구절 관리</h6>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <select
            className="form-control form-control-sm"
            style={{ width: 120 }}
            value={testamentFilter}
            onChange={(e) => setTestamentFilter(e.target.value)}
          >
            <option value="all">전체</option>
            <option value="ot">구약</option>
            <option value="nt">신약</option>
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate('/bible-json-upload')}
          >
            JSON 업로드
          </button>
        </div>
      </div>

      <div className="card-body">
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        {loading ? (
          <div className="text-center py-5 text-muted">불러오는 중…</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-hover mb-0">
              <thead className="thead-light">
                <tr>
                  <th style={{ width: 56 }}>#</th>
                  <th>개역개정</th>
                  <th>영어</th>
                  <th style={{ width: 90 }}>구약/신약</th>
                  <th>책이름</th>
                  <th style={{ width: 100 }}>전체 장수</th>
                  <th style={{ width: 100 }}>전체 절수</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      표시할 책이 없습니다.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={row.code}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(`/bible/${encodeURIComponent(row.code)}?version=${KO_VERSION}`)
                      }
                    >
                      <td>{row.sort_order ?? idx + 1}</td>
                      <td>
                        <div className="font-weight-bold">{row.koName}</div>
                        <div className="small text-muted">
                          NKRV {row.nkrvVerses.toLocaleString()}절
                        </div>
                      </td>
                      <td>
                        <div>{row.enName}</div>
                        <div className="small text-muted">
                          {enVersionCode
                            ? `${enVersionCode} ${row.enVerses.toLocaleString()}절`
                            : '영어 버전 없음'}
                        </div>
                      </td>
                      <td>{row.testament}</td>
                      <td>
                        <span className="font-weight-bold">{row.koName}</span>
                        <span className="text-muted ml-1">({row.code})</span>
                      </td>
                      <td className="text-right">{row.stdChapter ?? '-'}</td>
                      <td className="text-right">{row.stdVerse ?? '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BibleList;
